import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { Seo } from '@/components/seo/Seo';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const Account = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAnonymous } = useAuth();
  const { toast } = useToast();
  const { plan, credits, planStart, planEnd, loading } = useCredits();
  const [submitting, setSubmitting] = useState(false);
  const [hasPending, setHasPending] = useState(false);

  useEffect(() => {
    // Anonymous visitors don't have an account — send them to beta access application.
    if (!authLoading && (!user || isAnonymous)) navigate('/beta_access');
  }, [authLoading, user, isAnonymous, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('credit_requests')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .limit(1);
      if (!cancelled) setHasPending(!!(data && data.length > 0));
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleRequestCredits = async () => {
    if (!user) return;
    trackEvent('request_more_credits_clicked', {
      source: 'account',
      status: 'submitted',
    });
    setSubmitting(true);

    // Block if there is already a pending request for this user
    const { data: existingPending, error: pendingErr } = await supabase
      .from('credit_requests')
      .select('id, requested_option, created_at')
      .eq('user_id', user.id)
      .eq('status', 'pending')
      .limit(1);

    if (pendingErr) {
      setSubmitting(false);
      toast({ title: 'Request failed', description: pendingErr.message, variant: 'destructive' });
      return;
    }

    if (existingPending && existingPending.length > 0) {
      setSubmitting(false);
      toast({
        title: 'Request already pending',
        description: 'You already have a pending credit request. Please wait for an admin to review it.',
        variant: 'destructive',
      });
      return;
    }

    const { data: inserted, error } = await supabase
      .from('credit_requests')
      .insert({
        user_id: user.id,
        requested_option: 'more_credits',
        status: 'pending',
      })
      .select('id')
      .single();
    setSubmitting(false);
    if (error) {
      toast({ title: 'Request failed', description: error.message, variant: 'destructive' });
      return;
    }
    // Notify operator (fire-and-forget)
    if (inserted?.id) {
      supabase.functions
        .invoke('notify-credit-request', {
          body: { requestId: inserted.id, origin: window.location.origin },
        })
        .catch((e) => console.error('notify-credit-request failed:', e));
    }
    setHasPending(true);
    toast({
      title: 'Request submitted',
      description: 'An admin will review your credit request shortly.',
    });
  };

  return (
    <>
      <Seo title="My Account: Applyt" description="View your plan, credits and usage." />
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />
        <section className="max-w-3xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            My Account
          </h1>
          <p className="text-muted-foreground mb-8">{user?.email}</p>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Current Plan</span>
                <Badge variant="secondary" className="capitalize">{loading ? '…' : plan}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wide">Credits left</div>
                <div className="text-2xl font-semibold flex items-center gap-1.5 mt-1">
                  <Zap className="h-5 w-5 text-primary" />
                  {loading ? '—' : credits}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wide">Documents</div>
                <div className="mt-2">
                  <Badge variant="default">Allowed</Badge>
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wide">Plan period</div>
                <div className="mt-1 text-sm">
                  {formatDate(planStart)} → {formatDate(planEnd)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-center p-6">
              {hasPending ? (
                <Button variant="outline" disabled>
                  Pending review
                </Button>
              ) : (
                <Button onClick={handleRequestCredits} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Request more credits'}
                </Button>
              )}
            </CardContent>
          </Card>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default Account;
